/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import {
  ActivityIndicator,
  PanResponder,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { useShellScreenHeaderRefresh } from '../../shell/navigation/TabScreenRefreshContext';
import { useSessionStore } from '../../shell/session/store/useSessionStore'
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { colors } from '../../shared/theme/colors'
import { ticketColors } from '../../shared/theme/ticketColors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { matchesSearchText } from '../../shared/format/searchText';
import { CONTENT_WIDE_BREAKPOINT, WEB_SIDEBAR_BREAKPOINT } from '../../shared/layout/breakpoints';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import {
  DINING_TABLE_STATUS_LABELS,
  type DiningTable,
  type DiningTableStatus,
  type TableDraftSummary,
  type TableSession,
} from '../domain/hospitalityTypes';
import { mesaCardMatchesFilter, mesaCardViewModel, type MesaCardFilter } from '../domain/mesaCardViewModel';
import { mesaVisualStateMeta } from '../domain/mesaVisualStatus';
import { KpiTile, mesaStateAccent, MiniBadge, StatePill } from '../ui/mesasPrimitives';
import {
  assignTableWaiter,
  deleteDiningTable,
  fetchHospitalitySnapshot,
  listBranchUsers,
  mergeDiningTables,
  openTableSession,
  reorderDiningTables,
  setDiningTablePosition,
  splitDiningTable,
  staffAckTableGuestSignals,
  staffGetTableDraft,
  syncTableSessionKitchenSentFromOpenTickets,
  staffCancelTablesidePayment,
  getOpenTableSession,
  transferTableSession,
  updateDiningTableStatus,
  upsertDiningTable,
} from '../infra/hospitalityRepo';
import { TableGuestInboxPanel } from './TableGuestInboxPanel';
import { TablesideSettingsPanel } from './TablesideSettingsPanel';
import { TableQrPackSection } from './TableQrPackSection';
import { TablesideMetricsSection } from './TablesideMetricsSection';
import { TableSumarCartaModal } from './TableSumarCartaModal';
import { getCachedStoreProfile } from '../../settings/application/storefrontStatus';
import { guestActivityBadgeBits } from '../domain/guestActivityBadgeBits';
import { setTablesideCheckoutBaseline } from '../domain/tablesideCheckoutBaseline';
import { commitTablesidePosHandoff } from '../domain/tablesidePosHandoff';
import { buildTableCheckoutCart } from '../application/buildTableCheckoutCart';
import { setPosFulfillmentType } from '../../shared/pos/posHandoffSettings';
import { useMesasLiveRefresh } from '../hooks/useMesasLiveRefresh';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { showInfo } from '../../shared/ui/appToast'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm'
import { useScreenRefresh } from '../../shared/ui/hooks-screen/useScreenRefresh';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { ModalShell } from '../../shared/modals/ModalShell';
import { ModalFormField } from '../../shared/modals/ModalFormField';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { usePosStore, type CartItem } from '../../pos/app-api';

const DINING_TABLE_STATUSES: DiningTableStatus[] = ['available', 'occupied', 'reserved', 'closed'];

const MAP_TABLE_W = 110;
const MAP_TABLE_H = 72;
const MAP_TABLE_GAP = 14;

/** Stable auto-layout from full table list (not filtered). Avoids overlap with card size. */
function autoMapPosition(
  index: number,
  total: number,
  mapWidth: number
): { x: number; y: number; cols: number; rows: number } {
  const n = Math.max(1, total);
  const usableW = Math.max(MAP_TABLE_W, mapWidth > 0 ? mapWidth - spacing.md * 2 : MAP_TABLE_W * 4);
  const cols = Math.max(1, Math.min(n, Math.floor(usableW / (MAP_TABLE_W + MAP_TABLE_GAP)) || Math.ceil(Math.sqrt(n))));
  const rows = Math.max(1, Math.ceil(n / cols));
  const col = index % cols;
  const row = Math.floor(index / cols);
  const xPad = 0.02;
  const yPad = 0.04;
  const cardXFrac = mapWidth > 0 ? MAP_TABLE_W / mapWidth : 0.12;
  const usableX = Math.max(0.05, 1 - xPad * 2 - cardXFrac);
  const usableY = Math.max(0.05, 1 - yPad * 2 - 0.12);
  const x =
    cols === 1
      ? xPad + usableX / 2
      : xPad + (col / Math.max(1, cols - 1)) * usableX;
  const y =
    rows === 1
      ? yPad + usableY / 2
      : yPad + (row / Math.max(1, rows - 1)) * usableY;
  return {
    x: Math.min(0.88, Math.max(0, x)),
    y: Math.min(0.82, Math.max(0, y)),
    cols,
    rows,
  };
}

export function DiningTablesScreen() {
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isWide = width >= CONTENT_WIDE_BREAKPOINT;
  const isModalDesktop = width >= WEB_SIDEBAR_BREAKPOINT;
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const branches = useSessionStore((s) => s.branches);
  const branchName = branches.find((b) => b.id === currentBranchId)?.name ?? 'Mi local';
  const canViewTables = useModulePermission('Tables', 'view');
  const canManageLayout = useModulePermission('Tables', 'manage_layout');
  const canOperate = useModulePermission('Tables', 'operate');
  const canReopen = useModulePermission('Tables', 'reopen');
  const tablesGate = useCapabilityGate('tables', {
    title: 'Mesas no disponibles',
    subtitle: 'Este rubro no tiene mesas.',
  });
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [storeSlug, setStoreSlug] = useState<string | null>(null);
  const [storeLogoUrl, setStoreLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCapacity, setNewCapacity] = useState('4');

  const [editOpen, setEditOpen] = useState(false);
  const [sumarOpen, setSumarOpen] = useState(false);
  /** Mesa de la carta Sumar (sobrevive si la ficha se cierra en el handoff). */
  const [sumarTable, setSumarTable] = useState<{ id: string; name: string } | null>(null);
  const sumarHandoffRef = React.useRef(false);
  const [editTable, setEditTable] = useState<DiningTable | null>(null);
  const [editName, setEditName] = useState('');
  const [editCapacity, setEditCapacity] = useState('4');
  const [editStatus, setEditStatus] = useState<DiningTableStatus>('available');

  const [saving, setSaving] = useState(false);
  const [tablesWithReady, setTablesWithReady] = useState<Set<string>>(new Set());
  const [tableDraftById, setTableDraftById] = useState<Map<string, TableDraftSummary>>(new Map());
  const [branchUsers, setBranchUsers] = useState<Array<{ id: string; name: string }>>([]);
  const [waiterAssignLoading, setWaiterAssignLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'map' | 'sort'>('grid');
  const [showMergePicker, setShowMergePicker] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [splitName, setSplitName] = useState('');
  const [showSplitForm, setShowSplitForm] = useState(false);
  const [mergeSplitLoading, setMergeSplitLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [cardFilter, setCardFilter] = useState<MesaCardFilter>('all');
  const [showTransferPicker, setShowTransferPicker] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState<string | null>(null);
  const [editPosX, setEditPosX] = useState('');
  const [editPosY, setEditPosY] = useState('');
  const [openSessionTableIds, setOpenSessionTableIds] = useState<Set<string>>(new Set());
  const [sessionLineCountById, setSessionLineCountById] = useState<Map<string, number>>(new Map());
const [sessionByTableId, setSessionByTableId] = useState<Map<string, TableSession>>(new Map());
const [kitchenCountsById, setKitchenCountsById] = useState<Map<string, { open: number; ready: number; unpaid: boolean }>>(new Map());
const [reservationByTableId, setReservationByTableId] = useState<Map<string, { timeLabel: string | null; overdue: boolean }>>(new Map());
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const [showSalonSetup, setShowSalonSetup] = useState(false);
  const [showTableSettings, setShowTableSettings] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [editNeedsLiberate, setEditNeedsLiberate] = useState(false);
  const [editCanCobrar, setEditCanCobrar] = useState(false);
  const [mapSize, setMapSize] = useState({ width: 0, height: 0 });
  /** Live drag preview while moving a table on the map. */
  const [dragPreview, setDragPreview] = useState<{ id: string; x: number; y: number } | null>(null);

  const canChangeStatus = canOperate || canReopen;
  const { confirm, ConfirmModal } = useOperationalConfirm();
  /** Confirm dentro del ModalShell de editar mesa (overlay). El confirm raíz no se ve encima del sheet. */
  const { confirm: confirmInSheet, ConfirmModal: ConfirmInSheetModal } = useOperationalConfirm();
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const OCCUPIED_WARN_MS = 60 * 60 * 1000; // 60 min
  const OCCUPIED_DANGER_MS = 90 * 60 * 1000; // 90 min

  const tableOccupiedDurationMs = useCallback((table: DiningTable): number => {
    if (table.status !== 'occupied' || !table.occupiedAt) return 0;
    const guest = tableDraftById.get(table.id);
    const sessionLines = sessionLineCountById.get(table.id) ?? 0;
    const hasGuest = Boolean(
      guest &&
        (guest.draftLineCount > 0 || guest.openSignalCount > 0 || guest.lookingGuestCount > 0),
    );
    const hasReady = tablesWithReady.has(table.id);
    const hasOpenSession = openSessionTableIds.has(table.id);
    // Sesión abierta vacía (toque a libre → POS) sin guest ni cocina: no mostrar reloj.
    if (hasOpenSession && sessionLines === 0 && !hasGuest && !hasReady) return 0;
    const occupiedMs = new Date(table.occupiedAt).getTime();
    if (!Number.isFinite(occupiedMs)) return 0;
    return Math.max(0, nowMs - occupiedMs);
  }, [nowMs, openSessionTableIds, sessionLineCountById, tableDraftById, tablesWithReady]);

  const formatDuration = (ms: number): string => {
    const totalMin = Math.floor(ms / 60_000);
    if (totalMin < 1) return '<1 min';
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h < 1) return `${m} min`;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  const syncTables = useCallback(async () => {
    if (!currentBranchId) {
      setTables([]);
      setTablesWithReady(new Set());
      setTableDraftById(new Map());
      setOpenSessionTableIds(new Set());
      setSessionLineCountById(new Map());
      setSessionByTableId(new Map());
      setKitchenCountsById(new Map());
      setReservationByTableId(new Map());
      return;
    }
    const snapshot = await fetchHospitalitySnapshot(currentBranchId);
    setTables(snapshot.diningTables);
    setOpenSessionTableIds(new Set(snapshot.tableSessions.map((s) => s.diningTableId)));
    const sessions = new Map<string, TableSession>();
    const sessionLines = new Map<string, number>();
    for (const s of snapshot.tableSessions) {
      sessions.set(s.diningTableId, s);
      sessionLines.set(s.diningTableId, s.lineCount ?? (Array.isArray(s.cartJson) ? s.cartJson.length : 0));
    }
    setSessionByTableId(sessions);
    setSessionLineCountById(sessionLines);
    const readyIds = new Set<string>();
    const kitchenCounts = new Map<string, { open: number; ready: number; unpaid: boolean }>();
    for (const ticket of snapshot.kitchenTickets) {
      const tid = ticket.diningTableId;
      if (!tid) continue;
      const c = kitchenCounts.get(tid) ?? { open: 0, ready: 0, unpaid: false };
      if (ticket.status === 'ready') {
        c.ready += 1;
        readyIds.add(tid);
      } else if (ticket.status === 'pending' || ticket.status === 'preparing') {
        c.open += 1;
      }
      if (ticket.paymentState === 'pending' && ticket.status !== 'cancelled' && ticket.status !== 'served') {
        c.unpaid = true;
      }
      kitchenCounts.set(tid, c);
    }
    setKitchenCountsById(kitchenCounts);
    setTablesWithReady(readyIds);
    const drafts = new Map<string, TableDraftSummary>();
    for (const d of snapshot.tableDrafts ?? []) {
      drafts.set(d.diningTableId, d);
    }
    setTableDraftById(drafts);
    const now = Date.now();
    const reservations = new Map<string, { timeLabel: string | null; overdue: boolean; when: number }>();
    for (const r of snapshot.tableReservations ?? []) {
      const status = (r.status || '').toLowerCase();
      if (status !== 'pending' && status !== 'confirmed') continue;
      const when = new Date(`${r.reservationDate}T${r.reservationTime}`).getTime();
      if (!Number.isFinite(when)) continue;
      const entry = {
        timeLabel: r.reservationTime ? r.reservationTime.slice(0, 5) : null,
        overdue: when < now,
        when,
      };
      const prev = reservations.get(r.diningTableId);
      if (!prev || when < prev.when) reservations.set(r.diningTableId, entry);
    }
    setReservationByTableId(new Map(
      [...reservations].map(([k, v]) => [k, { timeLabel: v.timeLabel, overdue: v.overdue }]),
    ));
    if (canOperate) {
      try {
        setBranchUsers(await listBranchUsers(currentBranchId));
      } catch {
        setBranchUsers([]);
      }
    }
  }, [canOperate, currentBranchId]);

  useEffect(() => {
    if (!currentBranchId) {
      setStoreSlug(null);
      setStoreLogoUrl(null);
      return;
    }
    void getCachedStoreProfile(currentBranchId)
      .then((profile) => {
        setStoreSlug(profile?.slug?.trim() || null);
        setStoreLogoUrl(profile?.logoUrl?.trim() || null);
      })
      .catch(() => {
        setStoreSlug(null);
        setStoreLogoUrl(null);
      });
  }, [currentBranchId]);

  const loadTables = useCallback(async () => {
    if (!currentBranchId) {
      setTables([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      await syncTables();
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.load', error, userMessage: 'No se pudieron cargar las mesas.' });
    } finally {
      setLoading(false);
    }
  }, [currentBranchId, syncTables]);

  const { refreshing: pullRefreshing, onRefresh } = useScreenRefresh(async () => {
    if (!currentBranchId) return { silent: true };
    await syncTables();
  });

  const syncTablesSilent = useCallback(async () => {
    try {
      await syncTables();
    } catch {
      // Live poll/realtime: no toast spam
    }
  }, [syncTables]);

  useMesasLiveRefresh(currentBranchId, () => void syncTablesSilent(), {
    enabled: canViewTables,
  });

  useFocusEffect(
    useCallback(() => {
      if (!canViewTables) return;
      void loadTables();
    }, [canViewTables, loadTables]),
  );

  useShellScreenHeaderRefresh('DiningTables', onRefresh);

  const resetAdd = useCallback(() => {
    setNewName('');
    setNewCapacity('4');
    setAddOpen(false);
  }, []);

  const resetEdit = useCallback(() => {
    // Handoff ficha→carta: onClose del Modal puede dispararse al ocultar la ficha.
    if (sumarHandoffRef.current) {
      setEditOpen(false);
      return;
    }
    setEditTable(null);
    setEditName('');
    setEditCapacity('4');
    setEditStatus('available');
    setEditPosX('');
    setEditPosY('');
    setShowTransferPicker(false);
    setTransferTargetId(null);
    setShowTableSettings(false);
    setShowMoreActions(false);
    setShowMergePicker(false);
    setShowSplitForm(false);
    setMergeTargetId(null);
    setSplitName('');
    setEditNeedsLiberate(false);
    setEditCanCobrar(false);
    setSumarOpen(false);
    setSumarTable(null);
    setEditOpen(false);
  }, []);

  const openSumarCarta = useCallback(() => {
    if (!editTable || !canOperate) return;
    // Ref ANTES de setState: si ocultar la ficha llama onClose/resetEdit, no mata la carta.
    sumarHandoffRef.current = true;
    setSumarTable({ id: editTable.id, name: editTable.name });
    setSumarOpen(true);
    setEditOpen(false);
  }, [canOperate, editTable]);

  const closeSumarCarta = useCallback(() => {
    const handoffTable = sumarTable;
    sumarHandoffRef.current = false;
    setSumarOpen(false);
    setSumarTable(null);
    if (handoffTable) {
      // Reabrir ficha con la mesa de la carta (no depender de editTable stale/null).
      setEditTable((prev) => {
        if (prev?.id === handoffTable.id) return prev;
        const fromList = tables.find((t) => t.id === handoffTable.id);
        return fromList ?? prev;
      });
      setEditOpen(true);
    }
    void syncTables();
  }, [sumarTable, syncTables, tables]);

  const closeEditModal = useCallback(() => {
    if (sumarHandoffRef.current || sumarOpen) {
      setEditOpen(false);
      return;
    }
    resetEdit();
  }, [resetEdit, sumarOpen]);

  const parseCapacity = (value: string) => Math.max(1, Number(value) || 4);

  const handleAddTable = async () => {
    const name = newName.trim();
    if (!currentBranchId || !canManageLayout) return;
    if (!name) {
      showInfo('Falta el nombre', 'Poné cómo se llama la mesa.');
      return;
    }
    const capacityRaw = newCapacity.trim();
    if (capacityRaw && !/^\d+$/.test(capacityRaw)) {
      showInfo('Capacidad', 'Usá solo números (comensales).');
      return;
    }
    setSaving(true);
    try {
      const tableId = await upsertDiningTable({
        branchId: currentBranchId,
        name,
        capacity: parseCapacity(newCapacity),
      });
      const nextIndex = tables.length;
      const slot = autoMapPosition(nextIndex, nextIndex + 1, mapSize.width || 720);
      try {
        await setDiningTablePosition(tableId, slot.x, slot.y);
      } catch {
        // Position seed is best-effort; table still created.
      }
      resetAdd();
      await loadTables();
      showInfo('Mesa agregada', name);
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.create', error, userMessage: 'No se pudo crear la mesa.' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTable = async () => {
    if (!editTable || !currentBranchId) return;
    const name = editName.trim();
    if (!name) return;

    const statusChanged = editStatus !== editTable.status;
    if (
      statusChanged &&
      openSessionTableIds.has(editTable.id) &&
      editStatus !== 'occupied'
    ) {
      const ok = await confirmInSheet({
        title: 'Cambiar estado de mesa',
        message: `La mesa ${editTable.name} tiene una sesión abierta. Si pasás a "${DINING_TABLE_STATUS_LABELS[editStatus]}", la cuenta seguirá activa en POS pero el estado del salón cambiará.`,
        confirmLabel: 'Cambiar estado',
        destructive: true,
        iconName: 'warning-outline',
        presentation: 'overlay',
      });
      if (!ok) return;
    }

    setSaving(true);
    try {
      const detailsChanged =
        name !== editTable.name || parseCapacity(editCapacity) !== editTable.capacity;

      if (statusChanged) {
        await updateDiningTableStatus({
          tableId: editTable.id,
          status: editStatus,
          branchId: currentBranchId,
        });
      }

      if (detailsChanged && canManageLayout) {
        await upsertDiningTable({
          branchId: currentBranchId,
          name,
          capacity: parseCapacity(editCapacity),
          tableId: editTable.id,
        });
      }

      if (canManageLayout && (editPosX.trim() !== '' || editPosY.trim() !== '')) {
        const xPct = Math.min(100, Math.max(0, Number(editPosX) || 0));
        const yPct = Math.min(100, Math.max(0, Number(editPosY) || 0));
        await setDiningTablePosition(editTable.id, xPct / 100, yPct / 100);
      }

      resetEdit();
      await loadTables();
      showInfo('Mesa actualizada', name);
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.update', error, userMessage: 'No se pudo actualizar la mesa.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTable = useCallback(async () => {
    if (!editTable || !currentBranchId || !canManageLayout) return;
    const ok = await confirmInSheet({
      title: 'Eliminar mesa',
      message: `¿Seguro que querés eliminar ${editTable.name}? Las ventas históricas la perderán la referencia.`,
      confirmLabel: 'Eliminar',
      destructive: true,
      iconName: 'trash-outline',
      presentation: 'overlay',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await deleteDiningTable({ tableId: editTable.id, branchId: currentBranchId });
      resetEdit();
      await loadTables();
      showInfo('Mesa eliminada', editTable.name);
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.delete', error, userMessage: 'No se pudo eliminar la mesa.' });
    } finally {
      setSaving(false);
    }
  }, [editTable, currentBranchId, canManageLayout, confirmInSheet, resetEdit, loadTables]);

  const handleAssignWaiter = useCallback(
    async (userId: string | null) => {
      if (!editTable) return;
      setWaiterAssignLoading(true);
      try {
        await assignTableWaiter(editTable.id, userId);
        await loadTables();
        showInfo('Mozo asignado', userId ? 'Mozo actualizado' : 'Mozo desasignado');
      } catch (error) {
        presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.assign_waiter', error, userMessage: 'No se pudo asignar el mozo.' });
      } finally {
        setWaiterAssignLoading(false);
      }
    },
    [editTable, loadTables],
  );

  const handleMergeTable = useCallback(
    async (targetId: string) => {
      if (!editTable || !currentBranchId) return;
      const target = tables.find((t) => t.id === targetId);
      const ok = await confirmInSheet({
        title: 'Combinar mesas',
        message: `¿Unir "${editTable.name}" con "${target?.name ?? 'mesa destino'}"? Se juntan la cuenta, el pedido pendiente y las comandas. Los cubiertos se suman.`,
        confirmLabel: 'Combinar',
        destructive: true,
        iconName: 'git-merge-outline',
        presentation: 'overlay',
      });
      if (!ok) return;
      setMergeSplitLoading(true);
      try {
        await mergeDiningTables(editTable.id, targetId);
        await loadTables();
        setShowMergePicker(false);
        setEditOpen(false);
        showInfo('Mesas combinadas', 'La cuenta y el pedido quedaron en la mesa destino');
      } catch (error) {
        presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.combine', error, userMessage: 'No se pudo combinar.' });
      } finally {
        setMergeSplitLoading(false);
      }
    },
    [editTable, currentBranchId, loadTables, tables, confirmInSheet],
  );

  const handleSplitTable = useCallback(
    async () => {
      if (!editTable || !splitName.trim()) return;
      const ok = await confirmInSheet({
        title: 'Dividir mesa',
        message: `¿Crear la mesa "${splitName.trim()}" y pasarle los tickets pendientes de "${editTable.name}"?`,
        confirmLabel: 'Dividir',
        destructive: true,
        iconName: 'git-branch-outline',
        presentation: 'overlay',
      });
      if (!ok) return;
      setMergeSplitLoading(true);
      try {
        await splitDiningTable(editTable.id, splitName.trim());
        await loadTables();
        setShowSplitForm(false);
        setSplitName('');
        setEditOpen(false);
        showInfo('Mesa dividida', 'Se creó una nueva mesa con los tickets pendientes');
      } catch (error) {
        presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.split', error, userMessage: 'No se pudo dividir.' });
      } finally {
        setMergeSplitLoading(false);
      }
    },
    [editTable, splitName, loadTables, confirmInSheet],
  );

  const openEdit = useCallback(
    async (table: DiningTable) => {
      setEditTable(table);
      setEditName(table.name);
      setEditCapacity(String(table.capacity));
      setEditStatus(table.status);
      setEditPosX(table.posX != null ? String(Math.round(table.posX * 100)) : '');
      setEditPosY(table.posY != null ? String(Math.round(table.posY * 100)) : '');
      setShowTransferPicker(false);
      setTransferTargetId(null);
      setEditOpen(true);
      // Load branch users for waiter picker
      if (currentBranchId && canOperate) {
        try {
          const users = await listBranchUsers(currentBranchId);
          setBranchUsers(users);
        } catch {
          setBranchUsers([]);
        }
      }
    },
    [currentBranchId, canOperate],
  );

  const goToPosForTable = useCallback(
    async (table: DiningTable) => {
      if (!currentBranchId || !canOperate) return;
      const openKey = String(Date.now());
      setPosFulfillmentType('dine_in');
      if (openSessionTableIds.has(table.id)) {
        setEditOpen(false);
        navigateInShell(navigation, 'POS', { diningTableId: table.id, tableOpenKey: openKey });
        return;
      }
      try {
        await openTableSession({
          branchId: currentBranchId,
          diningTableId: table.id,
          covers: table.capacity,
        });
        setOpenSessionTableIds((prev) => new Set([...prev, table.id]));
        setEditOpen(false);
        navigateInShell(navigation, 'POS', { diningTableId: table.id, tableOpenKey: openKey });
      } catch (error) {
        showInfo('No se pudo abrir', error instanceof Error ? error.message : 'Reintentá.');
      }
    },
    [canOperate, currentBranchId, navigation, openSessionTableIds],
  );

  const cancelPendingPayment = useCallback(async (tableId: string) => {
    const session = sessionByTableId.get(tableId) ?? (await getOpenTableSession(tableId));
    if (!session) return;
    const cancelOk = await confirmInSheet({
      title: 'Cancelar pago desde el celu',
      message:
        'Si el comensal ya pagó, el cobro se acredita igual por webhook. Cancelar solo habilita cobrar en caja sin el intento pendiente.',
      confirmLabel: 'Cancelar pago',
      destructive: true,
      iconName: 'card-outline',
      presentation: 'overlay',
    });
    if (!cancelOk) return;
    await staffCancelTablesidePayment(session.id);
    await syncTables();
  }, [confirmInSheet, sessionByTableId]);

  const openPosForRound = useCallback(async () => {
    if (!editTable || !currentBranchId || !canOperate) return;
    if (!editCanCobrar) {
      showInfo(editTable.name, 'No hay nada nuevo para cobrar. Sumá o Liberá.');
      return;
    }
    const tableId = editTable.id;
    const openKey = String(Date.now());
    try {
      const openSession = await getOpenTableSession(tableId);
      if (openSession?.paymentStatus === 'pending') {
        const cancelOk = await confirmInSheet({
          title: 'Pago desde el celu en curso',
          message:
            'Un comensal está pagando con Mercado Pago. Si cobrás vos acá, cancelamos ese pago pendiente.',
          confirmLabel: 'Cancelar pago y cobrar',
          destructive: true,
          iconName: 'card-outline',
          presentation: 'overlay',
        });
        if (!cancelOk) return;
        await staffCancelTablesidePayment(openSession.id);
      }

      // Preview: merge cuenta abierta + Pendiente en el carrito POS sin volcar el borrador.
      // Si el mozo sale sin cobrar, Pendiente sigue en la ficha.
      await syncTableSessionKitchenSentFromOpenTickets(tableId).catch(() => {});
      const payload = await staffGetTableDraft(currentBranchId, tableId);
      const cartItems = buildTableCheckoutCart(payload);
      if (cartItems.length === 0 && editNeedsLiberate) {
        showInfo(editTable.name, 'No hay nada nuevo para cobrar. Sumá o Liberá.');
        void syncTables();
        return;
      }
      setTablesideCheckoutBaseline(cartItems);
      commitTablesidePosHandoff({
        branchId: currentBranchId,
        diningTableId: tableId,
        openKey,
        checkoutPreview: true,
        cart: cartItems,
      });
      // Mesa = salón: comanda/ticket como Comer acá (no Para llevar).
      setPosFulfillmentType('dine_in');
      usePosStore.getState().replaceCart(cartItems);
      setOpenSessionTableIds((prev) => new Set([...prev, tableId]));
      setSumarOpen(false);
      setEditOpen(false);
      navigateInShell(navigation, 'POS', {
        diningTableId: tableId,
        tableOpenKey: openKey,
        tablesideCheckoutPreview: true,
      });
      void syncTables();
    } catch (error) {
      showInfo('No se pudo abrir', error instanceof Error ? error.message : 'Reintentá.');
    }
  }, [
    canOperate,
    currentBranchId,
    editCanCobrar,
    editNeedsLiberate,
    editTable,
    navigation,
    syncTables,
    confirmInSheet,
  ]);

  const openOrResumeTable = useCallback(
    async (table: DiningTable) => {
      if (!currentBranchId || !canOperate) {
        void openEdit(table);
        return;
      }
      if (table.status === 'closed' || table.status === 'reserved') {
        void openEdit(table);
        return;
      }
      // Salón: siempre ficha (Sumar / Cobrar). No abrir POS al tocar libre.
      void openEdit(table);
    },
    [canOperate, currentBranchId, openEdit],
  );

  const handleMoveTable = useCallback(
    async (table: DiningTable, direction: 'up' | 'down') => {
      if (!currentBranchId || !canManageLayout) return;
      const sorted = [...tables].sort((a, b) => a.sortOrder - b.sortOrder);
      const idx = sorted.findIndex((t) => t.id === table.id);
      if (idx < 0) return;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sorted.length) return;
      // Swap positions
      const reordered = [...sorted];
      const tmp = reordered[idx];
      reordered[idx] = reordered[swapIdx];
      reordered[swapIdx] = tmp;
      const newIds = reordered.map((t) => t.id);
      try {
        await reorderDiningTables(currentBranchId, newIds);
        await loadTables();
      } catch (error) {
        presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.reorder', error, userMessage: 'No se pudo reordenar.' });
      }
    },
    [tables, currentBranchId, canManageLayout, loadTables],
  );

  const handleMapTableRelease = useCallback(
    async (tableId: string, x: number, y: number) => {
      if (!canManageLayout) return;
      setDragPreview({ id: tableId, x, y });
      try {
        await setDiningTablePosition(tableId, x, y);
        await syncTables();
      } catch (error) {
        presentUnexpectedFailure({
          module: 'hospitality',
          op: 'dining_tables.move_on_map',
          error,
          userMessage: 'No se pudo mover la mesa.',
        });
      } finally {
        setDragPreview((current) => (current?.id === tableId ? null : current));
      }
    },
    [canManageLayout, syncTables],
  );

  const statusOptions = useMemo(
    () =>
      DINING_TABLE_STATUSES.map((status) => ({
        key: status,
        label: DINING_TABLE_STATUS_LABELS[status],
      })),
    [],
  );

  const cardViewById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof mesaCardViewModel>>();
    for (const t of tables) {
      map.set(t.id, mesaCardViewModel({
        capacity: t.capacity,
        tableStatus: t.status,
        occupiedAt: t.occupiedAt,
        waiterName: t.assignedUserId
          ? (branchUsers.find((u) => u.id === t.assignedUserId)?.name ?? null)
          : null,
        hasOpenSession: openSessionTableIds.has(t.id),
        sessionCovers: sessionByTableId.get(t.id)?.covers ?? null,
        sessionPaymentStatus: sessionByTableId.get(t.id)?.paymentStatus ?? null,
        draft: tableDraftById.get(t.id) ?? null,
        kitchen: kitchenCountsById.get(t.id) ?? { open: 0, ready: 0, unpaid: false },
        reservation: reservationByTableId.get(t.id) ?? null,
      }));
    }
    return map;
  }, [tables, branchUsers, openSessionTableIds, sessionByTableId, tableDraftById, kitchenCountsById, reservationByTableId]);

  const filterCounts = useMemo(() => {
    const counts: Record<MesaCardFilter, number> = {
      all: tables.length, free: 0, occupied: 0, paid: 0, reserved: 0,
      signals: 0, looking: 0, kitchen: 0, unpaid: 0,
    };
    for (const t of tables) {
      const v = cardViewById.get(t.id);
      if (!v) continue;
      (['free', 'occupied', 'paid', 'reserved', 'signals', 'looking', 'kitchen', 'unpaid'] as const)
        .forEach((f) => { if (mesaCardMatchesFilter(v, f)) counts[f] += 1; });
    }
    return counts;
  }, [tables, cardViewById]);

  const filteredTables = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    return tables.filter((t) => {
      if (cardFilter !== 'all') {
        const v = cardViewById.get(t.id);
        if (!v || !mesaCardMatchesFilter(v, cardFilter)) return false;
      }
      if (q && !matchesSearchText(t.name, tableSearch)) return false;
      return true;
    });
  }, [tables, tableSearch, cardFilter, cardViewById]);

  const layoutIndexById = useMemo(() => {
    const sorted = [...tables].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'es')
    );
    const map = new Map<string, number>();
    sorted.forEach((t, i) => map.set(t.id, i));
    return map;
  }, [tables]);

  const needsMapSetup = useMemo(
    () => tables.length > 0 && tables.every((t) => t.posX == null && t.posY == null),
    [tables]
  );

  /** Mesas sin posición guardada: en vista van a la zona "Sin ubicar"; al mover, aparecen punteadas en el canvas. */
  const mapUnpositionedTables = useMemo(
    () => filteredTables.filter((t) => t.posX == null || t.posY == null),
    [filteredTables],
  );

  const mapCanvasTables = useMemo(
    () =>
      layoutEditMode
        ? filteredTables
        : filteredTables.filter((t) => t.posX != null && t.posY != null),
    [filteredTables, layoutEditMode],
  );

  const mapAutoMeta = useMemo(() => {
    if (mapCanvasTables.length === 0) return { minHeight: 140 };
    const probe = autoMapPosition(0, mapCanvasTables.length, mapSize.width || 720);
    return {
      minHeight: Math.max(240, probe.rows * (MAP_TABLE_H + MAP_TABLE_GAP) + 48),
    };
  }, [mapCanvasTables.length, mapSize.width]);

  const handleTransferTable = useCallback(async () => {
    if (!editTable || !transferTargetId) return;
    const target = tables.find((t) => t.id === transferTargetId);
      const ok = await confirmInSheet({
        title: 'Cambiar de mesa',
        message: `¿Pasar la cuenta, el pedido pendiente y las comandas de "${editTable.name}" a "${target?.name ?? 'mesa destino'}"? Los clientes tienen que escanear el QR de la mesa nueva.`,
        confirmLabel: 'Cambiar',
        destructive: true,
        iconName: 'swap-horizontal-outline',
        presentation: 'overlay',
      });
    if (!ok) return;
    setMergeSplitLoading(true);
    try {
      await transferTableSession(editTable.id, transferTargetId);
      await loadTables();
      resetEdit();
      showInfo(
        'Mesa cambiada',
        'La cuenta pasó a la mesa destino. Que escaneen el QR de la mesa nueva.',
      );
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'dining_tables.move', error, userMessage: 'No se pudo cambiar de mesa.' });
    } finally {
      setMergeSplitLoading(false);
    }
  }, [editTable, transferTargetId, loadTables, resetEdit, tables, confirmInSheet]);

  if (!tablesGate.allowed) {
    return <ShellContentFrame style={styles.shell}>{tablesGate.blocked}</ShellContentFrame>;
  }

  if (!canViewTables) {
    return (
      <ShellContentFrame style={[styles.shell, !isWide && styles.shellCompact]}>
        <ModuleEmptyState
          variant="nest"
          title="Mesas no disponibles"
          subtitle="Pedile al dueño que te habilite Mesas."
        />
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={[styles.shell, !isWide && styles.shellCompact]}>
      {ConfirmModal}
      <ScrollView
        style={styles.pageScroll}
        contentContainerStyle={styles.pageScrollContent}
        scrollEnabled={!layoutEditMode}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        refreshControl={
          Platform.OS === 'web' ? undefined : (
            <RefreshControl
              refreshing={pullRefreshing}
              onRefresh={() => void onRefresh()}
              colors={[ticketColors.brandInk]}
              tintColor={ticketColors.brandInk}
            />
          )
        }
      >
      <View style={styles.hero}>
        <View style={[styles.heroRow, !isWide && styles.heroRowStack]}>
          <View style={styles.heroCopy}>
            <Text style={styles.heroKicker}>Salón</Text>
            <View style={styles.heroTitleRow}>
              <Text style={styles.heroTitle}>Mesas</Text>
              <View style={styles.livePill}>
                <View style={styles.liveDot} />
                <Text style={styles.livePillText}>En vivo</Text>
              </View>
            </View>
            <Text style={styles.heroSub}>
              {branchName ? `${branchName} · ` : ''}Las mesas libres abren la cuenta.
            </Text>
          </View>
          {canManageLayout ? (
            <TouchableOpacity
              style={[styles.heroCta, !isWide && styles.heroCtaFull]}
              onPress={() => setAddOpen(true)}
              accessibilityRole="button"
              accessibilityLabel="Agregar mesa"
              testID="hospitality.mesas.add-btn"
            >
              <Ionicons name="add" size={18} color={colors.surface} />
              <Text style={styles.heroCtaText}>Agregar mesa</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      {canManageLayout || canViewTables ? (
        <View style={styles.setupSection}>
          <TouchableOpacity
            style={styles.setupToggle}
            onPress={() => setShowSalonSetup((v) => !v)}
            accessibilityRole="button"
            accessibilityLabel="Configuración del salón"
          >
            <Ionicons
              name={showSalonSetup ? 'chevron-down' : 'chevron-forward'}
              size={16}
              color={ticketColors.ink2}
            />
            <Text style={styles.setupToggleLabel}>Configuración del salón</Text>
          </TouchableOpacity>
          {showSalonSetup ? (
            <>
              {canManageLayout ? (
                <>
                  <TablesideSettingsPanel branchId={currentBranchId ?? null} />
                  <TableQrPackSection
                    slug={storeSlug}
                    branchName={branchName}
                    storeName={branchName}
                    logoUrl={storeLogoUrl}
                    tables={tables}
                  />
                </>
              ) : null}
              {canViewTables ? <TablesideMetricsSection branchId={currentBranchId} /> : null}
            </>
          ) : null}
        </View>
      ) : null}
      {tables.length > 0 ? (
        <View style={styles.toolsPanel}>
          <View style={styles.toolsRow}>
            <View style={styles.searchWrap}>
              <Ionicons name="search-outline" size={16} color={ticketColors.ink3} />
              <TextInput
                style={styles.searchInput}
                value={tableSearch}
                onChangeText={setTableSearch}
                placeholder="Buscar mesa o reserva…"
                placeholderTextColor={ticketColors.ink3}
                accessibilityLabel="Buscar mesa o reserva"
                accessibilityRole="search"
              />
            </View>
            <View style={styles.segmented}>
              {(['grid', 'map', 'sort'] as const).map((mode) => {
                const selected = viewMode === mode;
                const modeIcon = mode === 'grid' ? 'grid-outline' : mode === 'map' ? 'map-outline' : 'swap-vertical-outline';
                const modeLabel = mode === 'grid' ? 'Grilla' : mode === 'map' ? 'Mapa' : 'Ordenar';
                return (
                  <TouchableOpacity
                    key={mode}
                    style={[styles.segmentedOption, selected && styles.segmentedOptionActive]}
                    onPress={() => {
                      setViewMode(mode);
                      if (mode !== 'map') setLayoutEditMode(false);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={`Ver ${modeLabel.toLowerCase()}`}
                  >
                    <Ionicons name={modeIcon} size={15} color={selected ? colors.surface : ticketColors.ink2} />
                    <Text style={[styles.segmentedText, selected && styles.segmentedTextActive]}>
                      {modeLabel}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            {viewMode === 'map' && canManageLayout ? (
              <TouchableOpacity
                style={[styles.viewToggleBtn, layoutEditMode && styles.viewToggleBtnActive]}
                onPress={() => setLayoutEditMode((current) => !current)}
                accessibilityRole="button"
                accessibilityLabel={layoutEditMode ? 'Terminar de mover mesas' : 'Mover mesas'}
              >
                <Text style={[styles.viewToggleLabel, layoutEditMode && styles.viewToggleLabelActive]}>
                  {layoutEditMode ? 'Listo' : 'Mover mesas'}
                </Text>
              </TouchableOpacity>
            ) : null}
          </View>
          <View style={styles.kpiStrip}>
            {([
              ['occupied', 'En mesa', 'teal'],
              ['paid', 'Por liberar', 'warn'],
              ['looking', 'Mirando carta', 'info'],
              ['kitchen', 'En cocina', 'ink'],
              ['unpaid', 'Sin cobrar', 'warn'],
              ['reserved', 'Reservas hoy', 'violet'],
            ] as const).map(([f, lbl, tone]) => (
              <KpiTile
                key={f}
                value={filterCounts[f]}
                label={lbl}
                tone={tone}
                active={cardFilter === f}
                onPress={() => setCardFilter(cardFilter === f ? 'all' : f)}
                testID={`hospitality.mesas.kpi-${f}`}
              />
            ))}
          </View>
          <View style={styles.filterChipsRow}>
            {([
              ['all', 'Todas'],
              ['free', 'Libres'],
              ['occupied', 'En mesa'],
              ['paid', 'Por liberar'],
              ['reserved', 'Reservadas'],
              ['signals', 'Con señales'],
            ] as const).map(([key, baseLabel]) => {
              const selected = cardFilter === key;
              const label = `${baseLabel} (${filterCounts[key]})`;
              return (
                <TouchableOpacity
                  key={key}
                  style={[styles.filterChip, selected && styles.filterChipSelected]}
                  onPress={() => setCardFilter(key)}
                  accessibilityRole="button"
                  accessibilityLabel={label}
                >
                  <Text style={[styles.filterChipLabel, selected && styles.filterChipLabelSelected]}>{label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      ) : null}
      {loading ? (
        <ActivityIndicator color={colors.accent} style={styles.loader} />
      ) : tables.length === 0 ? (
        <ModuleEmptyState
          variant="nest"
          kicker="Salón"
          title="Mapa del salón"
          subtitle="Armá las mesas del salón. Las libres abren la cuenta."
          primaryAction={
            canManageLayout
              ? { label: 'Crear mesas', onPress: () => setAddOpen(true) }
              : undefined
          }
          testID="hospitality.mesas.empty"
        />
      ) : viewMode === 'map' ? (
        <>
          {needsMapSetup && canManageLayout && !layoutEditMode ? (
            <View style={styles.mapSetupBanner}>
              <Text style={styles.mapSetupText}>
                Armá el mapa del salón. Con Mover mesas arrastrás cada una a su lugar.
              </Text>
              <TouchableOpacity
                style={styles.mapSetupCta}
                onPress={() => setLayoutEditMode(true)}
                accessibilityRole="button"
                accessibilityLabel="Armar mapa"
              >
                <Text style={styles.mapSetupCtaText}>Armar mapa</Text>
              </TouchableOpacity>
            </View>
          ) : null}
          <PaperSheet style={styles.mapSheet}>
          <View
            style={[
              styles.mapContainer,
              { minHeight: mapAutoMeta.minHeight },
              layoutEditMode && styles.mapContainerEditing,
            ]}
            onLayout={(event) => {
              const { width: mapWidth, height: mapHeight } = event.nativeEvent.layout;
              setMapSize({ width: mapWidth, height: mapHeight });
            }}
          >
            {layoutEditMode ? (
              <Text style={styles.mapEditHint}>
                Arrastrá las mesas a su lugar · Las punteadas todavía no están en el mapa · Listo cuando termines
              </Text>
            ) : mapCanvasTables.length > 0 ? (
              <Text style={styles.mapViewHint}>Elegí una mesa para ver su ficha. Las libres abren la cuenta.</Text>
            ) : (
              <Text style={styles.mapViewHint}>Todavía ninguna mesa está ubicada en el mapa.</Text>
            )}
            {mapCanvasTables.map((table) => {
            const visualState = cardViewById.get(table.id)?.state ?? 'free';
            const accent = mesaStateAccent(visualState);
            const layoutIdx = layoutIndexById.get(table.id) ?? 0;
            const auto = autoMapPosition(layoutIdx, Math.max(1, tables.length), mapSize.width);
            const baseX = table.posX ?? auto.x;
            const baseY = table.posY ?? auto.y;
            const x = dragPreview?.id === table.id ? dragPreview.x : baseX;
            const y = dragPreview?.id === table.id ? dragPreview.y : baseY;
            const panResponder = layoutEditMode
              ? PanResponder.create({
                  onStartShouldSetPanResponder: () => true,
                  onMoveShouldSetPanResponder: () => true,
                  onPanResponderTerminationRequest: () => false,
                  onPanResponderMove: (_event, gestureState) => {
                    if (!mapSize.width || !mapSize.height) return;
                    const nextX = Math.min(0.9, Math.max(0, baseX + gestureState.dx / mapSize.width));
                    const nextY = Math.min(0.85, Math.max(0, baseY + gestureState.dy / mapSize.height));
                    setDragPreview({ id: table.id, x: nextX, y: nextY });
                  },
                  onPanResponderRelease: (_event, gestureState) => {
                    if (!mapSize.width || !mapSize.height) return;
                    const nextX = Math.min(0.9, Math.max(0, baseX + gestureState.dx / mapSize.width));
                    const nextY = Math.min(0.85, Math.max(0, baseY + gestureState.dy / mapSize.height));
                    void handleMapTableRelease(table.id, nextX, nextY);
                  },
                  onPanResponderTerminate: () => {
                    setDragPreview((current) => (current?.id === table.id ? null : current));
                  },
                })
              : undefined;
              return (
              <Pressable
                key={table.id}
                style={({ pressed }) => [
                  styles.mapTable,
                  {
                    left: `${Math.min(90, Math.max(0, x * 100))}%`,
                    top: `${Math.min(85, Math.max(0, y * 100))}%`,
                    zIndex: dragPreview?.id === table.id ? 20 : 1,
                  },
                  layoutEditMode && (table.posX == null || table.posY == null) && styles.mapTableUnplaced,
                  pressed && !layoutEditMode && { opacity: 0.85 },
                ]}
                onPress={layoutEditMode ? undefined : () => void openOrResumeTable(table)}
                {...panResponder?.panHandlers}
                accessibilityLabel={`Mesa ${table.name}, ${mesaVisualStateMeta(visualState).label}`}
              >
                <View style={[styles.mapTableAccent, { backgroundColor: accent }]} />
                <View style={styles.mapTableBody}>
                  <Text style={styles.mapTableName} numberOfLines={2}>
                    {table.name}
                  </Text>
                  <Text style={[styles.mapTableStatus, { color: accent }]} numberOfLines={1}>
                    {mesaVisualStateMeta(visualState).label}
                  </Text>
                  {(() => {
                    const g = tableDraftById.get(table.id);
                    if (!g) return null;
                    const bits = guestActivityBadgeBits(g);
                    if (bits.length === 0) return null;
                    return (
                      <Text style={styles.mapGuestBadge} numberOfLines={2}>
                        {bits.join(' · ')}
                      </Text>
                    );
                  })()}
                  {canManageLayout && !layoutEditMode ? (
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation();
                        void openEdit(table);
                      }}
                      hitSlop={8}
                      accessibilityRole="button"
                      accessibilityLabel="Editar mesa"
                    >
                      <Text style={styles.editGearLabel}>Editar</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              </Pressable>
              );
            })}
          </View>
          {!layoutEditMode && mapUnpositionedTables.length > 0 ? (
            <View style={styles.unplacedZone} testID="hospitality.mesas.unplaced-zone">
              <Text style={styles.unplacedTitle}>
                Sin ubicar ({mapUnpositionedTables.length})
              </Text>
              {canManageLayout ? (
                <Text style={styles.unplacedHint}>
                  No aparecen en el mapa. Con Mover mesas las arrastrás a su lugar.
                </Text>
              ) : null}
              <View style={styles.unplacedChips}>
                {mapUnpositionedTables.map((table) => (
                  <TouchableOpacity
                    key={table.id}
                    style={styles.unplacedChip}
                    onPress={() => void openOrResumeTable(table)}
                    accessibilityRole="button"
                    accessibilityLabel={`Mesa ${table.name}, ${mesaVisualStateMeta(cardViewById.get(table.id)?.state ?? 'free').label} (sin ubicar)`}
                  >
                    <View
                      style={[
                        styles.unplacedDot,
                        { backgroundColor: mesaStateAccent(cardViewById.get(table.id)?.state ?? 'free') },
                      ]}
                    />
                    <Text style={styles.unplacedChipLabel}>{table.name}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          ) : null}
          </PaperSheet>
        </>
      ) : (
        <View style={styles.gridWrap}>
          {filteredTables.map((item) => {
            const occupiedMs = tableOccupiedDurationMs(item);
            const occupiedTone: 'ok' | 'warn' | 'danger' =
              occupiedMs >= OCCUPIED_DANGER_MS
                ? 'danger'
                : occupiedMs >= OCCUPIED_WARN_MS
                  ? 'warn'
                  : 'ok';
            const cardView = cardViewById.get(item.id) ?? mesaCardViewModel({
              capacity: item.capacity,
              tableStatus: item.status,
              occupiedAt: item.occupiedAt,
              waiterName: null,
              hasOpenSession: false,
              sessionCovers: null,
              sessionPaymentStatus: null,
              draft: null,
              kitchen: { open: 0, ready: 0, unpaid: false },
            });
            return (
              <Pressable
                key={item.id}
                style={({ pressed }) => [
                  styles.tableCard,
                  occupiedTone === 'warn' && styles.tableCardWarn,
                  occupiedTone === 'danger' && styles.tableCardDanger,
                  pressed && { opacity: 0.8 },
                ]}
                onPress={() => void openOrResumeTable(item)}
                accessibilityLabel={`Mesa ${item.name}`}
                testID="hospitality.mesas.table-card"
              >
                <View style={styles.tableCardHeader}>
                  <Text style={styles.tableName}>{item.name}</Text>
                  {occupiedMs > 0 ? (
                    <Text
                      style={[
                        styles.occupiedTime,
                        occupiedTone === 'warn' && styles.occupiedTimeWarn,
                        occupiedTone === 'danger' && styles.occupiedTimeDanger,
                      ]}
                      accessibilityLabel={`Ocupada hace ${formatDuration(occupiedMs)}`}
                    >
                      {formatDuration(occupiedMs)}
                    </Text>
                  ) : null}
                  {canManageLayout || canOperate ? (
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation();
                        void openEdit(item);
                      }}
                      hitSlop={8}
                      accessibilityRole="button"
                      accessibilityLabel="Editar mesa"
                    >
                      <Text style={styles.editGearLabel}>{canManageLayout ? 'Editar' : 'Ver'}</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
                <StatePill state={cardView.state} />
                <Text style={styles.tableMeta}>
                  {cardView.meta}
                  {cardView.hint ? (
                    <Text style={styles.cardCtaHint}>{' · '}{cardView.hint}</Text>
                  ) : null}
                </Text>
                {cardView.badges.length > 0 ? (
                  <View style={styles.cardBadgeRow}>
                    {cardView.badges.map((b) => (
                      <MiniBadge key={b.label} kind={b.kind} label={b.label} pulse={b.pulse} />
                    ))}
                  </View>
                ) : null}
                {(() => {
                  const g = tableDraftById.get(item.id);
                  if (!g || (g.openSignalCount ?? 0) === 0 || !canOperate) return null;
                  return (
                    <View style={styles.guestBadgeWrap}>
                      {(g.openSignalCount ?? 0) > 0 ? (
                        <TouchableOpacity
                          onPress={(e) => {
                            e.stopPropagation();
                            void (async () => {
                              try {
                                if (!currentBranchId) return;
                                const n = await staffAckTableGuestSignals(currentBranchId, item.id);
                                await syncTables();
                                showInfo(
                                  item.name,
                                  n > 0 ? 'Listo, ya lo atendiste.' : 'No había avisos abiertos.',
                                );
                              } catch (error) {
                                presentUnexpectedFailure({
                                  module: 'hospitality',
                                  op: 'mesas.ack_signals',
                                  error,
                                  userMessage: 'No se pudo marcar como atendido.',
                                });
                              }
                            })();
                          }}
                          hitSlop={8}
                          accessibilityRole="button"
                          accessibilityLabel="Ya lo atendí"
                          style={styles.ackSignalBtn}
                        >
                          <Text style={styles.ackSignalLabel}>Ya lo atendí</Text>
                        </TouchableOpacity>
                      ) : null}
                    </View>
                  );
                })()}
                {canManageLayout && viewMode === 'sort' && tables.length > 1 ? (
                  <View style={styles.reorderRow}>
                    <Text style={styles.reorderLabel}>Orden</Text>
                    <TouchableOpacity
                      onPress={(e) => { e.stopPropagation(); void handleMoveTable(item, 'up'); }}
                      style={styles.reorderBtn}
                      disabled={item.sortOrder === Math.min(...tables.map((t) => t.sortOrder))}
                      accessibilityRole="button"
                      accessibilityLabel="Mover mesa arriba"
                    >
                      <Text style={styles.reorderBtnLabel}>↑</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={(e) => { e.stopPropagation(); void handleMoveTable(item, 'down'); }}
                      style={styles.reorderBtn}
                      disabled={item.sortOrder === Math.max(...tables.map((t) => t.sortOrder))}
                      accessibilityRole="button"
                      accessibilityLabel="Mover mesa abajo"
                    >
                      <Text style={styles.reorderBtnLabel}>↓</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
              </Pressable>
            );
          })}
        </View>
      )}
      </ScrollView>

      <ModalShell
        visible={addOpen && canManageLayout}
        onClose={resetAdd}
        title="Nueva mesa"
        widthVariant="compact"
        scrollable={false}
        cardStyle={isModalDesktop ? styles.modalDesktopCard : undefined}
        footer={
          <View style={[styles.modalActions, !isModalDesktop && styles.modalActionsSheet]}>
            <TouchableOpacity style={styles.secondaryBtn} onPress={resetAdd} testID="hospitality.mesas.cancel-btn">
              <Text style={styles.secondaryLabel}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]}
              onPress={() => void handleAddTable()}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Guardar mesa"
              testID="hospitality.mesas.save-btn"
            >
              <Text style={styles.primaryLabel}>{saving ? 'Guardando…' : 'Guardar mesa'}</Text>
            </TouchableOpacity>
          </View>
        }
      >
          <View style={styles.formPanel} testID="hospitality.mesas.new-modal">
            <ModalFormField label="Nombre" required>
            <TextInput
              value={newName}
              onChangeText={setNewName}
              placeholder="Nombre (ej. Mesa 1)"
              placeholderTextColor={ticketColors.ink3}
              style={modalExperience.formInput}
              autoFocus
              accessibilityLabel="Nombre de la mesa"
              testID="hospitality.mesas.name-input"
            />
            </ModalFormField>
            <ModalFormField label="Capacidad">
            <TextInput
              value={newCapacity}
              onChangeText={(t) => setNewCapacity(t.replace(/[^\d]/g, ''))}
              placeholder="Capacidad"
              placeholderTextColor={ticketColors.ink3}
              keyboardType="number-pad"
              style={modalExperience.formInput}
              accessibilityLabel="Capacidad"
              testID="hospitality.mesas.capacity-input"
            />
            </ModalFormField>
          </View>
      </ModalShell>

      <ModalShell
        visible={editOpen && Boolean(editTable)}
        onClose={closeEditModal}
        onRequestClose={() => !saving && closeEditModal()}
        title={editTable?.name ?? 'Mesa'}
        widthVariant="comfortable"
        cardStyle={isModalDesktop ? styles.modalDesktopCard : undefined}
        scrollContentStyle={styles.modalScrollContent}
        overlay={ConfirmInSheetModal}
        footer={
          <View style={[styles.modalActions, !isModalDesktop && styles.modalActionsSheet]}>
            <TouchableOpacity
              style={styles.secondaryBtn}
              onPress={() => !saving && closeEditModal()}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Text style={styles.secondaryLabel}>Cerrar</Text>
            </TouchableOpacity>
            {canManageLayout && showTableSettings ? (
              <TouchableOpacity
                style={[styles.secondaryBtn, saving && styles.primaryBtnDisabled]}
                onPress={() => void handleUpdateTable()}
                disabled={saving}
                accessibilityRole="button"
                accessibilityLabel="Guardar cambios"
              >
                <Text style={styles.secondaryLabel}>{saving ? 'Guardando…' : 'Guardar'}</Text>
              </TouchableOpacity>
            ) : null}
            {canOperate && editTable ? (
              <>
                <TouchableOpacity
                  style={[styles.primaryBtn, saving && styles.primaryBtnDisabled]}
                  onPress={openSumarCarta}
                  disabled={saving}
                  accessibilityRole="button"
                  accessibilityLabel="Sumar"
                  testID="hospitality.mesas.open-sumar-btn"
                >
                  <Text style={styles.primaryLabel}>Sumar</Text>
                </TouchableOpacity>
                {editCanCobrar ? (
                  <TouchableOpacity
                    style={[styles.secondaryBtn, saving && styles.primaryBtnDisabled]}
                    onPress={() => void openPosForRound()}
                    disabled={saving}
                    accessibilityRole="button"
                    accessibilityLabel="Cobrar"
                    testID="hospitality.mesas.open-pos-btn"
                  >
                    <Text style={styles.secondaryLabel}>Cobrar</Text>
                  </TouchableOpacity>
                ) : null}
              </>
            ) : null}
          </View>
        }
      >
          <View style={styles.formPanel}>
            {editTable && currentBranchId ? (
              <TableGuestInboxPanel
                branchId={currentBranchId}
                diningTableId={editTable.id}
                tableName={editTable.name}
                tableCapacity={editTable.capacity}
                canOperate={canOperate}
                confirm={confirmInSheet}
                liveRevision={(() => {
                  const g = tableDraftById.get(editTable.id);
                  const accountN = sessionLineCountById.get(editTable.id) ?? 0;
                  const open = openSessionTableIds.has(editTable.id) ? 1 : 0;
                  if (!g) return `acct:${accountN}:o${open}:r${tablesWithReady.has(editTable.id) ? 1 : 0}`;
                  return [
                    g.draftId ?? '',
                    g.draftUpdatedAt ?? '',
                    g.draftLineCount,
                    g.openSignalCount,
                    g.hasReadyToOrder ? 1 : 0,
                    g.hasCallWaiter ? 1 : 0,
                    g.lookingGuestCount,
                    accountN,
                    open,
                    tablesWithReady.has(editTable.id) ? 1 : 0,
                  ].join('|');
                })()}
                onChanged={syncTables}
                onSumar={openSumarCarta}
                waiterName={
                  editTable.assignedUserId
                    ? (branchUsers.find((u) => u.id === editTable.assignedUserId)?.name ?? null)
                    : null
                }
                onAssignWaiter={() => setShowMoreActions(true)}
                paymentPending={sessionByTableId.get(editTable.id)?.paymentStatus === 'pending'}
                onCancelPayment={() => void cancelPendingPayment(editTable.id)}
                onAccountMeta={(meta) => {
                  setEditNeedsLiberate(meta.needsLiberate);
                  setEditCanCobrar(meta.canCobrar);
                }}
              />
            ) : null}

            {canOperate && editTable ? (
              <View style={styles.settingsBlock}>
                <TouchableOpacity
                  style={styles.settingsToggle}
                  onPress={() => setShowMoreActions((v) => !v)}
                  accessibilityRole="button"
                  accessibilityLabel={showMoreActions ? 'Ocultar más acciones' : 'Más acciones de salón'}
                  testID="hospitality.mesas.more-actions"
                >
                  <Text style={styles.settingsToggleLabel}>
                    {showMoreActions ? 'Ocultar más acciones' : 'Más acciones · mozo, mover, combinar'}
                  </Text>
                </TouchableOpacity>
                {showMoreActions ? (
                  <View style={styles.settingsBody}>
                    {branchUsers.length > 0 ? (
                      <View style={styles.waiterSection}>
                        <Text style={styles.waiterLabel}>Mozo asignado</Text>
                        <View style={styles.waiterChips}>
                          <TouchableOpacity
                            style={[styles.waiterChip, !editTable?.assignedUserId && styles.waiterChipActive]}
                            onPress={() => void handleAssignWaiter(null)}
                            disabled={waiterAssignLoading}
                          >
                            <Text style={[styles.waiterChipLabel, !editTable?.assignedUserId && styles.waiterChipLabelActive]}>
                              Sin asignar
                            </Text>
                          </TouchableOpacity>
                          {branchUsers.map((u) => {
                            const selected = editTable?.assignedUserId === u.id;
                            return (
                              <TouchableOpacity
                                key={u.id}
                                style={[styles.waiterChip, selected && styles.waiterChipActive]}
                                onPress={() => void handleAssignWaiter(u.id)}
                                disabled={waiterAssignLoading}
                              >
                                <Text style={[styles.waiterChipLabel, selected && styles.waiterChipLabelActive]}>
                                  {u.name}
                                </Text>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      </View>
                    ) : null}

                    {(editTable.status === 'occupied' || openSessionTableIds.has(editTable.id) || editNeedsLiberate) ? (
                      <View style={styles.mergeSplitBlock}>
                        <TouchableOpacity
                          style={styles.mergeSplitBtn}
                          onPress={() => {
                            setShowTransferPicker((v) => !v);
                            setTransferTargetId(null);
                            setShowMergePicker(false);
                            setShowSplitForm(false);
                          }}
                          accessibilityRole="button"
                          accessibilityLabel="Cambiar de mesa"
                          testID="hospitality.mesas.transfer-btn"
                        >
                          <Text style={styles.mergeSplitBtnLabel}>Cambiar de mesa</Text>
                        </TouchableOpacity>
                        {showTransferPicker ? (
                          <View style={styles.mergePickerBox} testID="hospitality.mesas.transfer-picker">
                            <Text style={styles.mergePickerTitle}>Mesa libre destino</Text>
                            {tables.filter((t) => t.id !== editTable.id && t.status === 'available').length === 0 ? (
                              <Text style={styles.mergeHint}>No hay mesas libres. Liberá otra o creá una.</Text>
                            ) : (
                              tables
                                .filter((t) => t.id !== editTable.id && t.status === 'available')
                                .map((t) => (
                                  <TouchableOpacity
                                    key={t.id}
                                    style={[
                                      styles.mergeTargetChip,
                                      transferTargetId === t.id && styles.mergeTargetChipSelected,
                                    ]}
                                    onPress={() => setTransferTargetId(t.id)}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Pasar a ${t.name}`}
                                    testID={`hospitality.mesas.transfer-target.${t.id}`}
                                  >
                                    <Text style={styles.mergeTargetChipLabel}>{t.name}</Text>
                                  </TouchableOpacity>
                                ))
                            )}
                            <TouchableOpacity
                              style={[styles.primaryBtn, (!transferTargetId || mergeSplitLoading) && styles.primaryBtnDisabled]}
                              disabled={!transferTargetId || mergeSplitLoading}
                              onPress={() => void handleTransferTable()}
                              testID="hospitality.mesas.transfer-confirm"
                            >
                              <Text style={styles.primaryLabel}>
                                {mergeSplitLoading ? 'Moviendo…' : 'Confirmar cambio'}
                              </Text>
                            </TouchableOpacity>
                          </View>
                        ) : null}
                      </View>
                    ) : null}

                    {editTable.status === 'occupied' && tables.length > 1 ? (
                      <View style={styles.mergeSplitSection}>
                        {!showMergePicker && !showSplitForm ? (
                          <View style={styles.mergeSplitRow}>
                            <TouchableOpacity
                              style={styles.mergeSplitBtn}
                              onPress={() => {
                                setShowMergePicker(true);
                                setShowTransferPicker(false);
                                setShowSplitForm(false);
                              }}
                              accessibilityRole="button"
                              accessibilityLabel="Combinar con otra mesa"
                              testID="hospitality.mesas.merge-btn"
                            >
                              <Text style={styles.mergeSplitBtnLabel}>Combinar con otra mesa</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={styles.mergeSplitBtn}
                              onPress={() => {
                                setShowSplitForm(true);
                                setShowTransferPicker(false);
                                setShowMergePicker(false);
                              }}
                              accessibilityRole="button"
                              accessibilityLabel="Dividir mesa"
                              testID="hospitality.mesas.split-btn"
                            >
                              <Text style={styles.mergeSplitBtnLabel}>Dividir mesa</Text>
                            </TouchableOpacity>
                          </View>
                        ) : null}
                        {showMergePicker ? (
                          <View style={styles.mergePicker} testID="hospitality.mesas.merge-picker">
                            <Text style={styles.mergePickerTitle}>Seleccioná la mesa destino:</Text>
                            <Text style={styles.mergeHint}>
                              La cuenta y el pedido pendiente pasan a la mesa elegida. Cubiertos se suman.
                            </Text>
                            {tables
                              .filter((t) => t.id !== editTable?.id)
                              .map((t) => (
                                <TouchableOpacity
                                  key={t.id}
                                  style={[
                                    styles.mergeOption,
                                    mergeTargetId === t.id && styles.mergeOptionActive,
                                  ]}
                                  onPress={() => setMergeTargetId(t.id)}
                                  testID={`hospitality.mesas.merge-target.${t.id}`}
                                >
                                  <Text style={styles.mergeOptionLabel}>{t.name}</Text>
                                  <Text style={styles.mergeOptionStatus}>
                                    {DINING_TABLE_STATUS_LABELS[t.status]}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            <View style={styles.mergeActions}>
                              <TouchableOpacity
                                style={[styles.mergeActionBtn, styles.mergeActionConfirm]}
                                onPress={() => mergeTargetId && void handleMergeTable(mergeTargetId)}
                                disabled={!mergeTargetId || mergeSplitLoading}
                                testID="hospitality.mesas.merge-confirm"
                              >
                                <Text style={styles.mergeActionConfirmLabel}>Confirmar</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={styles.mergeActionBtn}
                                onPress={() => {
                                  setShowMergePicker(false);
                                  setMergeTargetId(null);
                                }}
                              >
                                <Text style={styles.mergeSplitBtnLabel}>Cancelar</Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        ) : null}
                        {showSplitForm ? (
                          <View style={styles.mergePicker}>
                            <Text style={styles.mergePickerTitle}>Nombre de la nueva mesa:</Text>
                            <TextInput
                              style={styles.splitInput}
                              value={splitName}
                              onChangeText={setSplitName}
                              placeholder="Ej: Mesa 5B"
                              placeholderTextColor={colors.textSecondary}
                              autoFocus
                            />
                            <View style={styles.mergeActions}>
                              <TouchableOpacity
                                style={[styles.mergeActionBtn, styles.mergeActionConfirm]}
                                onPress={() => void handleSplitTable()}
                                disabled={!splitName.trim() || mergeSplitLoading}
                              >
                                <Text style={styles.mergeActionConfirmLabel}>Dividir</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={styles.mergeActionBtn}
                                onPress={() => {
                                  setShowSplitForm(false);
                                  setSplitName('');
                                }}
                              >
                                <Text style={styles.mergeSplitBtnLabel}>Cancelar</Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                ) : null}
              </View>
            ) : null}

            {(canManageLayout || canChangeStatus) && (
              <View style={styles.settingsBlock}>
                <TouchableOpacity
                  style={styles.settingsToggle}
                  onPress={() => setShowTableSettings((v) => !v)}
                  accessibilityRole="button"
                  accessibilityLabel={showTableSettings ? 'Ocultar configuración del salón' : 'Configuración del salón'}
                >
                  <Text style={styles.settingsToggleLabel}>
                    {showTableSettings
                      ? 'Ocultar configuración del salón'
                      : 'Configuración · nombre, capacidad, mapa'}
                  </Text>
                </TouchableOpacity>
                {showTableSettings ? (
                  <View style={styles.settingsBody}>
                    <Text style={styles.settingsHint}>
                      Esto no es el día a día. Acá armás la mesa del salón (nombre, lugares, posición).
                    </Text>
                    <ModalFormField label="Nombre" required>
                      <TextInput
                        value={editName}
                        onChangeText={setEditName}
                        placeholder="Nombre"
                        placeholderTextColor={ticketColors.ink3}
                        style={[modalExperience.formInput, !canManageLayout && styles.inputDisabled]}
                        editable={canManageLayout}
                        accessibilityLabel="Nombre de la mesa"
                      />
                    </ModalFormField>
                    <ModalFormField label="Capacidad">
                      <TextInput
                        value={editCapacity}
                        onChangeText={setEditCapacity}
                        placeholder="Capacidad"
                        placeholderTextColor={ticketColors.ink3}
                        keyboardType="number-pad"
                        style={[modalExperience.formInput, !canManageLayout && styles.inputDisabled]}
                        editable={canManageLayout}
                        accessibilityLabel="Capacidad"
                      />
                    </ModalFormField>
                    {canManageLayout ? (
                      <View style={styles.advancedPosition}>
                        <Text style={styles.advancedPositionLabel}>Posición (avanzado)</Text>
                        <View style={styles.posRow}>
                          <TextInput
                            value={editPosX}
                            onChangeText={setEditPosX}
                            placeholder="Pos X %"
                            placeholderTextColor={colors.textMuted}
                            keyboardType="number-pad"
                            style={[styles.input, styles.posInput]}
                            accessibilityLabel="Posición X porcentaje"
                          />
                          <TextInput
                            value={editPosY}
                            onChangeText={setEditPosY}
                            placeholder="Pos Y %"
                            placeholderTextColor={colors.textMuted}
                            keyboardType="number-pad"
                            style={[styles.input, styles.posInput]}
                            accessibilityLabel="Posición Y porcentaje"
                          />
                        </View>
                      </View>
                    ) : null}
                    {canChangeStatus ? (
                      <View style={styles.statusRow}>
                        {statusOptions.map((option) => {
                          const selected = editStatus === option.key;
                          return (
                            <TouchableOpacity
                              key={option.key}
                              style={[styles.statusChip, selected && styles.statusChipSelected]}
                              onPress={() => setEditStatus(option.key)}
                              disabled={!canChangeStatus || saving}
                              accessibilityRole="radio"
                              accessibilityState={{ selected, disabled: !canChangeStatus || saving }}
                            >
                              <Text style={[styles.statusChipLabel, selected && styles.statusChipLabelSelected]}>
                                {option.label}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    ) : null}
                    {canManageLayout ? (
                      <TouchableOpacity
                        style={styles.dangerBtn}
                        onPress={() => void handleDeleteTable()}
                        disabled={saving}
                        accessibilityRole="button"
                        accessibilityLabel="Eliminar mesa"
                      >
                        <Text style={styles.dangerLabel}>Eliminar mesa</Text>
                      </TouchableOpacity>
                    ) : null}
                  </View>
                ) : null}
              </View>
            )}
          </View>
      </ModalShell>

      {sumarTable && currentBranchId ? (
        <TableSumarCartaModal
          visible={sumarOpen}
          branchId={currentBranchId}
          diningTableId={sumarTable.id}
          tableName={sumarTable.name}
          onClose={closeSumarCarta}
          onAdded={async () => {
            await syncTables();
          }}
        />
      ) : null}
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  filterBlock: { marginBottom: spacing.sm, gap: spacing.xs },
  searchInput: {
    flex: 1,
    paddingVertical: spacing.xs,
    ...typography.body,
    color: ticketColors.ink,
  },
  filterChipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  filterChip: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    backgroundColor: ticketColors.tile,
  },
  filterChipSelected: { borderColor: ticketColors.gold, backgroundColor: ticketColors.brandSoft },
  filterChipLabel: { ...typography.caption, color: ticketColors.ink2 },
  filterChipLabelSelected: { color: ticketColors.brandInk, fontWeight: '700' },
  posRow: { flexDirection: 'row', gap: spacing.sm },
  advancedPosition: { gap: spacing.xs },
  advancedPositionLabel: { ...typography.caption, color: ticketColors.ink2, fontWeight: '700' },
  posInput: { flex: 1 },
  mergeSplitBlock: { marginTop: spacing.sm, gap: spacing.xs },
  mergePickerBox: { gap: spacing.xs },
  mergeTargetChip: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
  },
  mergeTargetChipSelected: { borderColor: colors.accent },
  mergeTargetChipLabel: { ...typography.body, color: colors.textPrimary },
  viewToggleBtn: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
  },
  viewToggleBtnActive: { backgroundColor: ticketColors.gold, borderColor: ticketColors.gold },
  viewToggleLabel: { ...typography.body, color: colors.accent, fontWeight: '600' },
  viewToggleLabelActive: { color: '#FFFFFF' },
  mapSheet: { padding: spacing.xs },
  mapSetupBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexWrap: 'wrap',
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  mapSetupText: {
    ...typography.body,
    color: ticketColors.ink2,
    flex: 1,
    minWidth: 180,
  },
  mapSetupCta: {
    minHeight: 40,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    paddingHorizontal: spacing.md,
    justifyContent: 'center',
  },
  mapSetupCtaText: { ...typography.body, color: '#FFFFFF', fontWeight: '800' },
  mapContainer: {
    position: 'relative',
    minHeight: 500,
    backgroundColor: ticketColors.brandSoft,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    padding: spacing.md,
    paddingTop: spacing.xl,
  },
  mapContainerEditing: { borderStyle: 'dashed', borderColor: ticketColors.gold, borderWidth: 2 },
  mapEditHint: {
    ...typography.caption,
    color: ticketColors.brandInk,
    fontWeight: '800',
    alignSelf: 'center',
    marginBottom: spacing.sm,
  },
  mapViewHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
    alignSelf: 'center',
    marginBottom: spacing.sm,
  },
  mapTable: {
    position: 'absolute',
    width: MAP_TABLE_W,
    minHeight: MAP_TABLE_H,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  mapTableUnplaced: { borderStyle: 'dashed', borderColor: ticketColors.ink3 },
  unplacedZone: {
    marginTop: spacing.sm,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: ticketColors.rule,
    borderRadius: 12,
    padding: spacing.md,
    gap: spacing.xs,
    backgroundColor: ticketColors.tile,
  },
  unplacedTitle: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  unplacedHint: { ...typography.caption, color: colors.textMuted },
  unplacedChips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: 2 },
  unplacedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: colors.surface,
  },
  unplacedDot: { width: 8, height: 8, borderRadius: 4 },
  unplacedChipLabel: { ...typography.caption, color: colors.textPrimary, fontWeight: '700' },
  mapTableAccent: {
    width: 4,
    alignSelf: 'stretch',
  },
  mapTableBody: {
    flex: 1,
    paddingHorizontal: spacing.xs,
    paddingVertical: 6,
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 2,
  },
  mapTableName: { ...typography.caption, color: colors.textPrimary, fontWeight: '700', fontSize: 12 },
  mapTableStatus: { ...typography.caption, fontWeight: '700', fontSize: 11 },
  mapGuestBadge: {
    ...typography.caption,
    color: colors.accentStrong,
    fontWeight: '700',
    fontSize: 10,
    marginTop: 2,
  },
  addBtn: {
    backgroundColor: colors.accent,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  addBtnLabel: { ...typography.body, color: '#FFFFFF', fontWeight: '700' },
  loader: { marginTop: spacing.xl },
  shell: {
    ...ticketSurfaces.screen,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  shellCompact: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  pageScroll: { flex: 1 },
  pageScrollContent: { paddingBottom: spacing.xl, gap: spacing.sm },
  gridWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  tableCard: {
    flexBasis: '47%',
    flexGrow: 1,
    backgroundColor: ticketColors.tile,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    padding: spacing.md,
    minHeight: 110,
    gap: spacing.xs,
  },
  tableCardWarn: {
    borderColor: colors.warning,
    borderWidth: 2,
  },
  tableCardDanger: {
    borderColor: colors.danger,
    borderWidth: 2,
  },
  occupiedTime: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '700',
  },
  occupiedTimeWarn: {
    color: colors.warning,
  },
  occupiedTimeDanger: {
    color: colors.danger,
  },
  kpiStrip: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 10, marginTop: spacing.sm },
  cardBadgeRow: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: 5, minHeight: 20 },
  cardCtaHint: { ...typography.caption, color: '#115E59', fontWeight: '800' as const, textDecorationLine: 'underline' as const },
  tableCardHeader: {
    flexDirection: 'row' as const,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.xs,
  },
  editGearLabel: { ...typography.caption, color: ticketColors.gold, fontWeight: '700' },
  tableName: { ...typography.title3, color: ticketColors.ink, flexShrink: 1 },
  tableMeta: { ...typography.caption, color: ticketColors.ink2 },
  tableStatus: { ...typography.caption, color: colors.accent, fontWeight: '600' },
  guestBadgeWrap: {
    marginTop: 6,
    gap: 4,
    alignSelf: 'flex-start',
  },
  ackSignalBtn: {
    alignSelf: 'flex-start',
    marginTop: 2,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 10,
    backgroundColor: '#C2410C',
  },
  ackSignalLabel: {
    ...typography.caption,
    color: '#FFFFFF',
    fontWeight: '800',
  },
  reorderRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  reorderLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '700' },
  reorderBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reorderBtnLabel: { ...typography.body, color: colors.textSecondary, fontWeight: '700' },
  roundBtn: {
    borderRadius: 14,
    paddingVertical: spacing.md,
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.accent,
    marginTop: spacing.sm,
  },
  roundBtnLabel: { ...typography.body, color: colors.accent, fontWeight: '700' },
  waiterSection: { marginTop: spacing.sm },
  waiterLabel: { ...typography.caption, color: colors.textSecondary, fontWeight: '600', marginBottom: spacing.xs },
  waiterChips: { flexDirection: 'row' as const, flexWrap: 'wrap' as const, gap: spacing.xs },
  waiterChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  waiterChipActive: { borderColor: colors.accent, backgroundColor: colors.accent },
  waiterChipLabel: { ...typography.caption, color: colors.textSecondary },
  waiterChipLabelActive: { color: '#FFFFFF', fontWeight: '600' },
  mergeSplitSection: { marginTop: spacing.sm },
  mergeSplitRow: { flexDirection: 'row', gap: spacing.sm },
  mergeSplitBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    borderRadius: 10,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.controlSurface,
    alignItems: 'center',
  },
  mergeSplitBtnLabel: { ...typography.caption, color: colors.accent, fontWeight: '700' },
  mergePicker: { gap: spacing.xs },
  mergePickerTitle: { ...typography.caption, color: colors.textSecondary, fontWeight: '600' },
  mergeHint: { ...typography.caption, color: colors.textMuted, marginBottom: spacing.sm },
  mergeOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  mergeOptionActive: { borderColor: colors.accent },
  mergeOptionLabel: { ...typography.body, color: colors.textPrimary, fontWeight: '600' },
  mergeOptionStatus: { ...typography.caption, color: colors.textSecondary },
  mergeActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs },
  mergeActionBtn: { flex: 1, paddingVertical: spacing.sm, borderRadius: 8, alignItems: 'center' },
  mergeActionConfirm: { backgroundColor: colors.accent },
  mergeActionConfirmLabel: { ...typography.body, color: '#FFFFFF', fontWeight: '700' },
  splitInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    color: colors.textPrimary,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  modalCard: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    padding: spacing.lg,
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  modalTitle: { ...typography.title3, color: colors.textPrimary },
  input: {
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 10,
    padding: spacing.md,
    color: colors.textPrimary,
  },
  inputDisabled: {
    backgroundColor: colors.borderSoft,
    color: colors.textMuted,
  },
  modalActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  modalActionsWithDelete: { flexWrap: 'wrap' as const },
  tableHero: {
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  tableHeroMeta: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  settingsBlock: {
    marginTop: spacing.md,
    gap: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    paddingTop: spacing.md,
  },
  settingsToggle: {
    paddingVertical: spacing.xs,
  },
  settingsToggleLabel: {
    ...typography.body,
    color: colors.accent,
    fontWeight: '700',
  },
  settingsBody: {
    gap: spacing.md,
    paddingTop: spacing.sm,
  },
  settingsHint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  secondaryBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    borderRadius: 14,
    paddingVertical: spacing.md,
    alignItems: 'center',
    backgroundColor: colors.controlSurface,
  },
  secondaryLabel: { ...typography.body, color: colors.textPrimary, fontWeight: '600' },
  primaryBtn: {
    flex: 1,
    backgroundColor: colors.accent,
    borderRadius: 14,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  primaryBtnDisabled: { opacity: 0.6 },
  primaryLabel: { ...typography.body, color: '#FFFFFF', fontWeight: '700' },
  dangerBtn: {
    width: '100%',
    borderWidth: 1,
    borderColor: colors.danger,
    borderRadius: 14,
    paddingVertical: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.sm,
    backgroundColor: '#FEF2F2',
  },
  dangerLabel: { ...typography.body, color: colors.danger, fontWeight: '700' },
  statusRow: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: spacing.xs,
    marginVertical: spacing.sm,
  },
  statusChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  statusChipSelected: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  statusChipLabel: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  statusChipLabelSelected: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  hero: {
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 14,
    padding: spacing.md,
    marginBottom: spacing.sm,
  },
  heroRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.md },
  heroRowStack: { flexDirection: 'column', alignItems: 'stretch' },
  heroCopy: { flex: 1, minWidth: 0, gap: 3 },
  heroKicker: { ...typography.caption, color: ticketColors.ink3, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5 },
  heroTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
  heroTitle: { ...typography.titleXL, color: ticketColors.ink, fontWeight: '900', letterSpacing: -0.4 },
  livePill: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#E8F7F4', borderWidth: 1, borderColor: 'rgba(13,148,136,0.25)', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 3 },
  liveDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: '#0D9488' },
  livePillText: { fontSize: 11.5, fontWeight: '600', color: '#115E59' },
  heroSub: { ...typography.body, color: ticketColors.ink2, lineHeight: 21 },
  heroCta: {
    minHeight: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  heroCtaFull: { width: '100%' },
  heroCtaText: { ...typography.body, color: '#FFFFFF', fontWeight: '800' },
  setupSection: { marginBottom: spacing.xs },
  setupToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
  },
  setupToggleLabel: { color: ticketColors.ink2, fontSize: 13, fontWeight: '600' },
  toolsPanel: { gap: spacing.sm, marginBottom: spacing.sm },
  toolsRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
  segmented: { flexDirection: 'row', borderWidth: 1, borderColor: ticketColors.rule, borderRadius: 10, padding: 3, backgroundColor: ticketColors.tile },
  segmentedOption: { minHeight: 36, paddingHorizontal: spacing.sm, borderRadius: 7, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5 },
  segmentedOptionActive: { backgroundColor: ticketColors.gold },
  segmentedText: { ...typography.caption, color: ticketColors.ink2, fontWeight: '700' },
  segmentedTextActive: { color: '#FFFFFF' },
  searchWrap: { flex: 1, minWidth: 180, minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: ticketColors.rule, borderRadius: 10, backgroundColor: ticketColors.tile, paddingHorizontal: spacing.sm },
  formPanel: { borderRadius: 14, borderWidth: 1, borderColor: ticketColors.ruleSoft, backgroundColor: ticketColors.paper2, padding: spacing.md, gap: spacing.md },
  modalDesktopCard: { maxWidth: 640 },
  modalScrollContent: { padding: spacing.md },
  modalActionsSheet: { flexWrap: 'wrap' },
});
